import { FC, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Package, X, Check } from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import CustomModal from "../../../components/common/custom-modal/custom-modal";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  addBoxStorageAction,
  getAvailableBoxAction,
  getBoxStorageAction,
  removeBoxStorageAction,
} from "../../../redux/actions/boxStorageAction";
import { pxToRem } from "../../../util";
import {
  AddBoxStorageBody,
  BoxConfig,
} from "../../../typings/interfaces/responses/boxStorageResponse";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { Colors } from "../../../configs/colors";

dayjs.extend(utc);

interface BoxStorageModalProps {
  open: boolean;
  onClose: () => void;
  billIds: number[];
  onAddSuccess?: () => void;
}

const PERIODS = [
  { id: "6 Months" as const, title: "6 Months", subtitle: "Standard storage" },
  { id: "1 Year" as const, title: "1 Year", subtitle: "Long term" },
];

function OptionCard({
  title,
  subtitle,
  selected,
  disabled,
  onClick,
}: {
  title: string;
  subtitle: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        "relative min-h-[78px] rounded-2xl border px-4 py-3 text-left transition-all duration-150",
        disabled
          ? "cursor-not-allowed border-transparent bg-[#D9D9D9] text-white opacity-90"
          : selected
          ? "border-brand-secondary bg-brand-secondary text-white shadow-md"
          : "border-[#D9D9D9] bg-white text-[#515151] hover:border-brand-secondary/40 hover:shadow-sm",
      ].join(" ")}
    >
      {selected && !disabled ? (
        <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-white text-brand-secondary">
          <Check size={12} strokeWidth={3} />
        </span>
      ) : null}
      <div className="pr-5 text-sm font-bold">{title}</div>
      <div
        className={[
          "mt-1 text-[11px] font-medium",
          selected && !disabled ? "text-white/85" : disabled ? "text-white/90" : "text-[#515151]/60",
        ].join(" ")}
      >
        {subtitle}
      </div>
    </button>
  );
}

const BoxStorageModal: FC<BoxStorageModalProps> = ({
  open,
  onClose,
  billIds,
  onAddSuccess,
}) => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();

  const {
    addBoxStorageLoading,
    fetchedBoxStorage,
    fetchedBoxStorageLoading,
    removeBoxStorageLoading,
    boxesConfig,
  } = useAppSelector((state) => state.boxStorage);

  const { customer } = useAppSelector((s) => s.customer) ?? {};

  const [selectedSize, setSelectedSize] = useState<string>("SMALL");
  const [selectedPeriod, setSelectedPeriod] = useState<"6 Months" | "1 Year">(
    "6 Months"
  );

  const detectPeriod = (iso?: string): "6 Months" | "1 Year" => {
    if (!iso) return "6 Months";
    const now = dayjs().utc();
    const exp = dayjs(iso).utc();
    const diffMonths = exp.diff(now, "month", true);
    return diffMonths >= 10.5 ? "1 Year" : "6 Months";
  };

  const record = useMemo(
    () =>
      (fetchedBoxStorage?.body ?? []).find(
        (r: AddBoxStorageBody) =>
          Array.isArray(r.billIds) &&
          r.billIds.some((id) => billIds.includes(id))
      ) ?? null,
    [fetchedBoxStorage?.body, billIds.join(",")]
  );

  const computedExpiry = useMemo(() => {
    const months = selectedPeriod === "6 Months" ? 6 : 12;
    return dayjs().utc().add(months, "month").toISOString();
  }, [selectedPeriod]);

  const effectiveExpiryIso = record?.storageExpiryDate ?? computedExpiry;

  const busyAdd = addBoxStorageLoading || fetchedBoxStorageLoading;
  const busyRemove = removeBoxStorageLoading || fetchedBoxStorageLoading;
  const busy =
    addBoxStorageLoading || fetchedBoxStorageLoading || removeBoxStorageLoading;

  const resetUiToDefaults = () => {
    setSelectedSize("SMALL");
    setSelectedPeriod("6 Months");
  };

  useEffect(() => {
    dispatch(getAvailableBoxAction());
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) return;

    resetUiToDefaults();

    if (billIds.length > 0) {
      dispatch(getBoxStorageAction(billIds[0]));
    }
  }, [open, billIds.join(","), dispatch]);

  useEffect(() => {
    if (!open) return;

    if (!fetchedBoxStorageLoading) {
      if (record) prefillFromRecord(record);
      else resetUiToDefaults();
    }
  }, [open, record, fetchedBoxStorageLoading]);

  const getAliasFromBoxId = (boxId?: number): string => {
    if (!boxId || !boxesConfig?.body) return "";
    const box = boxesConfig.body.find((b) => b.id === boxId);
    return box?.alias ?? "";
  };

  const prefillFromRecord = (rec: AddBoxStorageBody) => {
    if (rec.boxId) {
      const alias = getAliasFromBoxId(rec.boxId);
      if (alias) setSelectedSize(alias);
    }
    setSelectedPeriod(detectPeriod(rec.storageExpiryDate));
  };

  const handleAdd = async () => {
    const selectedBox = boxesConfig?.body.find(
      (item) => item.alias === selectedSize
    );

    if (!selectedBox) {
      snackbar.showSnackbar("Please select a box size", "error");
      return;
    }

    const payload: AddBoxStorageBody = {
      customerId: customer?.id ?? 0,
      cashierId: 4,
      storeId: 2,
      merchantId: 1,
      billIds,
      boxId: selectedBox.id,
      storageExpiryDate: computedExpiry,
    };

    await dispatch(addBoxStorageAction(payload)).unwrap();
    await dispatch(getBoxStorageAction(billIds[0])).unwrap();
    snackbar.showSnackbar("Box has added successfully", "success");
    onAddSuccess?.();
  };

  const handleRemove = async () => {
    if (!record?.id) return;
    await dispatch(removeBoxStorageAction(record.id)).unwrap();
    await dispatch(getBoxStorageAction(billIds[0])).unwrap();
    snackbar.showSnackbar("Box has removed successfully", "success");
    onClose();
  };

  const boxes: BoxConfig[] = boxesConfig?.body ?? [];
  const totalAvailable = boxes.reduce((sum, b) => sum + (b.remainingSlots ?? 0), 0);
  const selectedBoxConfig = boxes.find((b) => b.alias === selectedSize);
  const selectedPeriodConfig = PERIODS.find((p) => p.id === selectedPeriod);
  const boxGridColumns = Math.min(Math.max(boxes.length, 1), 4);

  const boxCount = boxes.length;
  const modalHeight = pxToRem(
    boxCount > 8 ? 880 : boxCount > 4 ? 800 : 720
  );

  return (
    <CustomModal
      openModal={open}
      onClose={onClose}
      title=""
      hideCloseButton
      mode="popup"
      width={pxToRem(600)}
      height={modalHeight}
      sx={{ padding: 0, borderRadius: "1.5rem", overflow: "hidden" }}
    >
      <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
        <div className="flex items-start justify-between px-8 pb-4 pt-7">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-secondary/10 text-brand-secondary">
              <Package size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-[#515151]">Box Storage</h1>
              <p className="mt-1 text-sm font-medium text-[#515151]/60">
                Assign storage box size and rental period
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D9D9D9] text-[#515151] transition hover:bg-[#D9D9D9]/35"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 pb-7">
          <div className="rounded-3xl bg-[#F4F4F4] p-4">
            <div className="mb-3 px-1">
              <h2 className="text-base font-bold text-[#515151]">Box Sizes and Time Period</h2>
              <p className="mt-0.5 text-xs font-medium text-[#515151]/55">
                Unavailable options are locked automatically
              </p>
            </div>

            <section className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-[#D9D9D9]/60">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-extrabold text-[#515151]">Box Size</h3>
                <span className="rounded-full bg-brand/10 px-3 py-1 text-[11px] font-bold text-brand">
                  {totalAvailable} boxes available
                </span>
              </div>
              {boxes.length > 0 ? (
                <div
                  className="grid gap-3"
                  style={{ gridTemplateColumns: `repeat(${boxGridColumns}, 1fr)` }}
                >
                  {boxes.map((box) => (
                    <OptionCard
                      key={box.alias}
                      title={box.alias.charAt(0).toUpperCase() + box.alias.slice(1).toLowerCase()}
                      subtitle={
                        box.remainingSlots === 0
                          ? "Not Available"
                          : `Available: ${box.remainingSlots}`
                      }
                      selected={selectedSize === box.alias && box.remainingSlots !== 0}
                      disabled={box.remainingSlots === 0}
                      onClick={() => setSelectedSize(box.alias)}
                    />
                  ))}
                </div>
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={22} />
                </Box>
              )}
            </section>

            <section className="mt-4 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-[#D9D9D9]/60">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-extrabold text-[#515151]">Time Period</h3>
                <span className="text-[11px] font-semibold text-[#515151]/55">
                  Renewable after expiration
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {PERIODS.map((p) => (
                  <OptionCard
                    key={p.id}
                    title={p.title}
                    subtitle={p.subtitle}
                    selected={selectedPeriod === p.id}
                    disabled={busy}
                    onClick={() => setSelectedPeriod(p.id)}
                  />
                ))}
              </div>
            </section>
          </div>

          <div className="mt-5 rounded-2xl border border-[#D9D9D9] bg-white px-5 py-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-[#515151]/50">
                  Selection Summary
                </div>
                <div className="mt-1 text-sm font-bold text-[#515151]">
                  {selectedBoxConfig
                    ? `${selectedBoxConfig.alias.charAt(0).toUpperCase()}${selectedBoxConfig.alias.slice(1).toLowerCase()} box`
                    : "No box selected"}{" "}
                  · {selectedPeriodConfig?.title}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs font-semibold text-[#515151]/55">Expires on</div>
                <div className="text-sm font-extrabold text-brand">
                  {dayjs(effectiveExpiryIso).format("MMM D, YYYY")}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-5">
            <button
              type="button"
              onClick={handleRemove}
              disabled={!record?.id || busyRemove}
              className="flex h-14 items-center justify-center gap-2 rounded-2xl border-2 border-brand/35 bg-white text-lg font-extrabold text-brand/60 transition hover:bg-brand/5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busyRemove ? <CircularProgress size={22} sx={{ color: Colors.PRIMARY }} /> : "Remove"}
            </button>
            <button
              type="button"
              onClick={handleAdd}
              disabled={!!record?.id || busyAdd}
              className="flex h-14 items-center justify-center gap-2 rounded-2xl bg-brand text-lg font-extrabold text-white shadow-lg shadow-green-200 transition hover:opacity-95 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busyAdd ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Add"}
            </button>
          </div>
        </div>
      </Box>
    </CustomModal>
  );
};

export default BoxStorageModal;
